/**
 * Staged-folder name slug helpers, shared by the staged-folders table
 * (composites) and the AOP `@` popup's create row. A folder's slug is its
 * stored identity — the thing an AOP chip resolves against, and the sandbox
 * directory it mounts at (see STAGED_FOLDER_NAME_PATTERN in @quovy/types).
 *
 * The *display* direction is the package-wide `humanizeSlug`; only the
 * slugifier is staged-folder-specific, because each surface has its own
 * grammar and they are not interchangeable:
 *   - `sanitizeStagedFolderName` — STAGED_FOLDER_NAME_PATTERN,
 *     `/^[a-z0-9][a-z0-9_-]{0,63}$/` (hyphens legal, may start with a digit).
 *   - `slugifyVariableKey` (rich-chip-editor) — template variable keys,
 *     `/^[a-z][a-z0-9_]*$/` (hyphens become `_`, must start with a letter).
 *   - `slugify` (apps/web) — URL slugs, hyphen-separated.
 */

import { humanizeSlug } from "../../utils";

/** Mirrors the publish-time limit on a staged folder's `name`. */
export const MAX_STAGED_FOLDER_NAME_LENGTH = 64;

/**
 * Keep typed names path- and label-safe as the user types (see
 * STAGED_FOLDER_NAME_PATTERN). Length-capped here, the same way
 * `slugifyVariableKey` caps template field keys: publish-time validation
 * rejects names over 64 chars, and a slug helper that can emit a value its
 * own validator refuses puts the error a screen away from the typing.
 */
export function sanitizeStagedFolderName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "")
    .replace(/^[-_]+/, "")
    .slice(0, MAX_STAGED_FOLDER_NAME_LENGTH);
}

/**
 * Slug → sentence-case prose for display ("cash_tracker" → "Cash tracker").
 * The slug remains the stored identity and the thing an AOP chip resolves
 * against — and because the name cell re-derives the slug from this text on
 * every keystroke, the display MUST sanitize back to the slug it came from.
 * That is why hyphens stay: flattening "their-imports" to "Their imports"
 * meant one keystroke silently renamed the folder to `their_imports`,
 * orphaning every AOP ref that held the old name.
 */
export function humanizeStagedFolderName(slug: string): string {
  return humanizeSlug(slug);
}
